/**
 * Zone Configuration Merge Utilities
 *
 * Implements a scope-based three-layer merge strategy for zone configurations:
 * 1. AvailabilityType (Cross-Journey Defaults - broadest scope)
 * 2. Journey (Journey-Scoped Overrides - middle scope)
 * 3. Page (Page-Scoped Overrides - narrowest scope)
 *
 * MERGE PRINCIPLE: Narrower scope overrides broader scope
 * - Journey overrides AvailabilityType (journey-specific > cross-journey)
 * - Page overrides both (page-specific > journey-wide > cross-journey)
 *
 * KEY INSIGHT: All layers can define the same properties (API callbacks, labels, validation, actions).
 * The distinction is SCOPE OF APPLICABILITY, not capability.
 *
 * Merge strategies by zone type:
 * - 'actions': Array merge by matching 'id' property (supports all 3 layers)
 * - 'summaryContent': Shallow merge of properties (supports 2 layers only)
 *
 * For detailed explanation, see: SCOPE-BASED-MERGE-STRATEGY.md
 */

import { JourneyZone, ZoneActionConfig, JourneyActionOverride } from '../models/checkout-journey.types';

/**
 * Combine availability-type and journey configs
 * Handles three scenarios: availability only, journey only, or both
 *
 * @param zone - The zone type (e.g., 'actions', 'summaryContent')
 * @param availabilityConfig - Config from availability-type (BOOKING, REQUEST, etc.)
 * @param journeyConfig - Config from journey (UNION, MATCH, EVENT, etc.)
 * @returns Combined config (before page overrides)
 */
export function combineAvailabilityAndJourneyConfigs<T>(
  zone: JourneyZone,
  availabilityConfig: T | undefined,
  journeyConfig: T | undefined,
): T | undefined {
  // Neither exists
  if (!availabilityConfig && !journeyConfig) {
    return undefined;
  }

  // Only availability exists
  if (!journeyConfig) {
    return availabilityConfig;
  }

  // Only journey exists
  if (!availabilityConfig) {
    return journeyConfig;
  }

  // Both exist: merge based on zone type
  if (zone === 'actions') {
    return mergeZoneActions(availabilityConfig as ZoneActionConfig[], journeyConfig as JourneyActionOverride[]) as T;
  }

  if (zone === 'summaryContent') {
    return mergeSummaryContent(availabilityConfig, journeyConfig) as T;
  }

  // For other zones (if any in future), journey completely overrides
  return journeyConfig;
}

/**
 * Apply page-level overrides to base config
 * Only applies to 'actions' zone - other zones don't support page overrides yet
 *
 * @param zone - The zone type
 * @param baseConfig - Base config (already merged from availability + journey)
 * @param pageOverrides - Optional page-level overrides (e.g., validation, custom callbacks)
 * @returns Config with page overrides applied (if applicable)
 */
export function applyPageOverrides<T>(
  zone: JourneyZone,
  baseConfig: T | undefined,
  pageOverrides?: JourneyActionOverride[],
): T | undefined {
  // Only actions zone supports page overrides
  if (zone !== 'actions' || !pageOverrides || pageOverrides.length === 0 || !baseConfig) {
    return baseConfig;
  }

  return mergeZoneActions(baseConfig as ZoneActionConfig[], pageOverrides) as T;
}

/**
 * Merge summary content with partial overrides
 * Uses shallow merge strategy for property-level overrides (object is not deep nested, so shallow merge is sufficient)
 *
 * @param availabilityConfig - Complete summaryContent from availability-type config
 * @param journeyOverride - Partial summaryContent override from journey config
 * @returns Merged summaryContent configuration
 */
export function mergeSummaryContent<T>(
  availabilityConfig: T | undefined,
  journeyOverride: Partial<T> | undefined,
): T | undefined {
  if (!availabilityConfig) {
    return journeyOverride as T | undefined;
  }

  if (!journeyOverride) {
    return availabilityConfig;
  }

  // Shallow merge: journey properties override availability properties
  return { ...availabilityConfig, ...journeyOverride };
}

/**
 * Merge actions with overrides by matching 'id' property
 * Used for both journey-level and page-level merges
 *
 * Merge strategy:
 * 1. Match overrides to base actions by 'id' property (required in overrides)
 * 2. For matched actions: shallow merge properties (override wins on conflicts)
 * 3. Include all base actions (with or without overrides)
 * 4. Append unmatched overrides (those without matching 'id' in base actions)
 *
 * @param baseActions - Base actions from previous layer
 * @param overrides - Override actions (partial, with required 'id' for matching)
 * @returns Merged array of complete actions
 */
export function mergeZoneActions(
  baseActions: ZoneActionConfig[] | undefined,
  overrides: JourneyActionOverride[] | undefined,
): ZoneActionConfig[] {
  if (!baseActions) {
    return [];
  }

  if (!overrides || overrides.length === 0) {
    return baseActions;
  }

  // Build map of overrides by id for efficient lookup
  const overridesById = new Map<string, JourneyActionOverride>();
  const overridesWithoutMatchingId: JourneyActionOverride[] = [];

  overrides.forEach((override) => {
    overridesById.set(override.id, override);
  });

  // Merge base actions with overrides
  const mergedActions = baseActions.map((baseAction) => {
    if (baseAction.id && overridesById.has(baseAction.id)) {
      const override = overridesById.get(baseAction.id)!;
      // Shallow merge: override properties win on conflicts
      return { ...baseAction, ...override } as ZoneActionConfig;
    }
    return baseAction;
  });

  // Find overrides that don't match any base action
  overrides.forEach((override) => {
    const hasMatch = baseActions.some((action) => action.id === override.id);
    if (!hasMatch) {
      overridesWithoutMatchingId.push(override);
    }
  });

  // Append unmatched overrides (treat as new complete actions if they have required properties)
  // Note: TypeScript will enforce that these are valid ZoneActionConfig at usage site
  return [...mergedActions, ...(overridesWithoutMatchingId as ZoneActionConfig[])];
}
